// KeyVaultService with AWS KMS (Envelope Encryption) + Secrets Manager
// - AES256GCM parent class
// - KeyVaultService: integrates with KMS and Secrets Manager
// - Supports Solana / EVM / Tron (abstracted)
// - Data Key rotation and secret rewrap
//
// Dependencies (install):
// npm install @aws-sdk/client-kms @aws-sdk/client-secrets-manager @aws-sdk/util-base64-node
// npm install tweetnacl @solana/web3.js ethers tronweb // optional: for chain wallet helpers
//
// Environment:
// - AWS_REGION
// - KMS_KEY_ID (the CMK used to generate/decrypt data keys)

import {
  KMSClient,
  CreateKeyCommand,
  GenerateDataKeyCommand,
  DecryptCommand,
  CreateAliasCommand,
  ScheduleKeyDeletionCommand,
  CancelKeyDeletionCommand,
  DeleteAliasCommand,
} from '@aws-sdk/client-kms';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { SESClient, SendEmailCommand } from '@aws-sdk/client-ses';

export function fromHex(hex: string) {
  return new Uint8Array(
    hex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) ?? []
  );
}

export function toHex(bytes: Uint8Array) {
  return Buffer.from(bytes).toString('hex');
}

export class KMS {
  kms: KMSClient;
  constructor({ region }: { region: string }) {
    this.kms = new KMSClient({ region });
  }

  async createKey({ description }: { description: string }) {
    const command = new CreateKeyCommand({
      Description: description,
    });
    return await this.kms.send(command);
  }

  async createAlias(aliasName: string, keyId: string) {
    const command = new CreateAliasCommand({
      AliasName: aliasName,
      TargetKeyId: keyId,
    });
    return await this.kms.send(command);
  }

  async generateDataKey(keyId: string) {
    const command = new GenerateDataKeyCommand({
      KeyId: keyId,
      KeySpec: 'AES_256',
    });

    const response = await this.kms.send(command);
    if (!response.CiphertextBlob) {
      throw new Error('Failed to generate data key');
    }

    return {
      ciphertext: toHex(response.CiphertextBlob),
    };
  }

  async decryptDataKey(ciphertext: string) {
    const command = new DecryptCommand({
      CiphertextBlob: fromHex(ciphertext),
    });
    const response = await this.kms.send(command);
    if (!response.Plaintext) {
      throw new Error('Failed to decrypt data key');
    }
    return {
      plaintext: toHex(response.Plaintext),
    };
  }
}

const s3 = new S3Client({
  region: 'ap-southeast-1',
});

export const upload = async (
  blob: Blob,
  options: {
    key: string;
    expires_at?: string;
  }
) => {
  try {
    if (process.env.BUCKET_DOMAIN_NAME && process.env.BUCKET_NAME) {
      const key = options.key;
      const command = new PutObjectCommand({
        Bucket: process.env.BUCKET_NAME,
        Key: key, // 上傳 S3 內的位置
        Body: Buffer.from(await blob.arrayBuffer()), // 檔案內容
        ContentType: blob.type, // 設定為圖片 mime type
        ACL: 'public-read', // 如果要公開訪問
      });

      const response = await s3.send(command);

      if (response.$metadata.httpStatusCode !== 200) {
        throw new Error('Failed to upload file to S3');
      }

      return `https://${process.env.BUCKET_DOMAIN_NAME}/${key}`;
    } else {
      const formData = new FormData();
      formData.append(
        'image',
        new File([blob], options.key, { type: blob.type })
      );

      if (options.expires_at) {
        formData.append('expires_at', options.expires_at);
      }
      const response = await fetch('https://s3-blush.vercel.app/images', {
        method: 'POST',
        body: formData,
      });
      const data = (await response.json()) as { url: string };
      return data.url;
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const ses = new SESClient({
  region: 'ap-southeast-1',
});

export const sendEmail = async (to: string, subject: string, body: string) => {
  try {
    const command = new SendEmailCommand({
      Destination: {
        ToAddresses: [to],
      },
      Message: {
        Body: {
          Text: {
            Data: body,
          },
          // 也可以用 Html (可同時存在 Text 與 Html)
          // Html: { Data: "<h1>Hello</h1><p>這是一封測試郵件</p>" }
        },
        Subject: { Data: subject },
      },
      Source: 'no-reply@moonpacket.com', // 必須是已驗證過的
    });

    return await ses.send(command);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
